"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Framework() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stages = [{
    code: "01 / DISCOVERY",
    name: "Discovery.",
    opening: "Every engagement begins with a deep dive into your operational reality. We audit your current workflows, identify bottlenecks in your communication, and map the technical requirements needed to modernize your backend infrastructure. The output is a strategic blueprint for your digital transformation.",
    items: [{
      title: "Workflow audit",
      body: "A comprehensive review of your existing manual processes, documenting the friction points that slow down your field operations."
    }, {
      title: "System mapping",
      body: "Identifying the right software stack for your specific trade, ensuring compatibility between CRM, accounting, and field tools."
    }, {
      title: "Requirement analysis",
      body: "Defining the technical boundaries and data needs to ensure your new systems are scalable and compliant with industry standards."
    }]
  }, {
    code: "02 / ARCHITECTURE",
    name: "Architecture.",
    opening: "We build your digital foundation from first principles. We configure your CRM, integrate your field service management tools, and automate your bookkeeping, ensuring every piece of software talks to the others before we go live.",
    items: [{
      title: "Pipeline construction",
      body: "Building custom communication flows that automate lead management and customer updates, reducing your administrative burden."
    }, {
      title: "Integration logic",
      body: "Connecting your disparate tools into a unified ecosystem, ensuring data flows seamlessly from the field to the office."
    }, {
      title: "System validation",
      body: "Rigorous testing of automated triggers and data syncs to ensure reliability under real-world operational conditions."
    }]
  }, {
    code: "03 / IMPLEMENTATION",
    name: "Implementation.",
    opening: "Systems are deployed to support your team, not to complicate their day. We focus on intuitive interfaces and clear hierarchies, ensuring your staff can adopt the new technology with minimal friction and maximum efficiency.",
    items: [{
      title: "System deployment",
      body: "Rolling out the configured software environment, including custom dashboards and automated reporting tools for your team."
    }, {
      title: "Process calibration",
      body: "Fine-tuning the software settings to match your specific trade requirements, ensuring the tools work exactly as you need."
    }, {
      title: "Team onboarding",
      body: "Providing the documentation and logic needed for your staff to master the new digital infrastructure with confidence."
    }]
  }, {
    code: "04 / OPTIMIZATION",
    name: "Optimization.",
    opening: "We remain involved through the initial launch phase. By monitoring system performance and gathering feedback from your team, we refine the automation and feed insights back into your operational strategy for long-term growth.",
    items: [{
      title: "Performance monitoring",
      body: "Direct oversight of system health, managing updates and troubleshooting any issues that arise during the transition."
    }, {
      title: "Feedback loops",
      body: "On-site or remote review of operational performance, ensuring the technology continues to deliver the expected ROI."
    }, {
      title: "Growth audit",
      body: "Post-implementation review of your digital maturity, documenting performance gains for future scaling and iteration."
    }]
  }];
  return <section data-section-id="15258" id="processtimeline" className="bg-background text-foreground py-24 md:py-44">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
        {}
        <div ref={intro.ref} className={`grid grid-cols-12 gap-6 transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h1 className="col-span-12 font-default font-medium leading-[0.92] tracking-tight" style={{
          fontSize: "clamp(3.5rem, 8vw, 7.5rem)"
        }}>
            The framework.
          </h1>
          <p className="col-span-12 md:col-span-7 col-start-1 mt-12 font-default font-normal leading-[1.45]" style={{
          fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)"
        }}>
            Every CSOS engagement follows a four-stage architectural process. 
            We transform your manual operations into a modern, automated digital infrastructure.
          </p>
        </div>

        {}
        <div className="mt-24 md:mt-32 h-px w-full bg-border" />

        {}
        <div className="flex flex-col">
          {stages.map((stage, i) => <StageBlock key={i} data-index={i} stage={stage} isLast={i === stages.length - 1} />)}
        </div>
      </div>
    </section>;
}
type StageBlockProps = {
  stage: {
    code: string;
    name: string;
    opening: string;
    items: {
      title: string;
      body: string;
    }[];
  };
  isLast: boolean;
  "data-index"?: number;
};
function StageBlock({
  stage,
  isLast,
  ...rest
}: StageBlockProps) {
  const block = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={block.ref} className="border-b border-border last:border-b-0">
      <div className="grid grid-cols-12 gap-6 py-24 md:py-24">
        {}
        <div className="col-span-12 md:col-span-3 md:col-start-1">
          <div className="md:sticky md:top-24">
            <span className={`block font-mono uppercase text-primary transition-all duration-500 ease-out ${block.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
            fontSize: "0.75rem",
            letterSpacing: "0.04em",
            lineHeight: 1.4
          }}>
              {stage.code}
            </span>
          </div>
        </div>

        {}
        <div className="col-span-12 md:col-span-8 md:col-start-5">
          {}
          <div className="overflow-hidden">
            <h2 className={`font-default font-medium leading-[0.98] tracking-tight transition-transform duration-[520ms] ease-out ${block.isVisible ? "translate-y-0" : "translate-y-full"}`} style={{
            fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
            transitionDelay: "120ms"
          }}>
              {stage.name}
            </h2>
          </div>

          {}
          <p className={`mt-12 font-default font-normal leading-[1.45] transition-all duration-700 ease-out ${block.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
          transitionDelay: "320ms"
        }}>
            {stage.opening}
          </p>

          {}
          <div className="mt-16 grid grid-cols-12 gap-6">
            {stage.items.map((item, j) => <SubDeliverable key={j} data-index={j} item={item} index={j} visible={block.isVisible} />)}
          </div>
        </div>
      </div>
    </div>;
}
type SubDeliverableProps = {
  item: {
    title: string;
    body: string;
  };
  index: number;
  visible: boolean;
  "data-index"?: number;
};
function SubDeliverable({
  item,
  index,
  visible,
  ...rest
}: SubDeliverableProps) {
  return <div {...rest} className={`col-span-12 sm:col-span-6 md:col-span-4 group transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
    transitionDelay: `${480 + index * 80}ms`
  }}>
      <div className="relative pb-3">
        <h4 className="font-default font-semibold leading-[1.25]" style={{
        fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
        letterSpacing: "-0.005em"
      }}>
          {item.title}
        </h4>
        <span className="absolute bottom-0 left-0 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
      </div>
      <p className="mt-4 font-default font-normal text-muted-foreground leading-[1.55]" style={{
      fontSize: "clamp(0.9375rem, 1vw, 1.0625rem)"
    }}>
        {item.body}
      </p>
    </div>;
}